/*
  Shared reader for the product-options apps, used by the gift carousel
  (lunicora-product-gifts.js) and the cart drawer picker
  (lunicora-cart-drawer.js) so both mirror the product page:

  - Easify Product Options: window.TPOConfigs.product_options_easify holds
    every option set; a product gets the sets whose conditions it matches,
    exactly like the app decides on the product page. Every option a shopper
    can fill in (swatches, buttons, dropdowns, text boxes) is returned with
    its required flag; modals, spacers and dividers are not order data and
    are skipped.
  - Customily: products with a Customily personalizer cannot be reproduced
    outside their own page, so callers can ask whether a product has one and
    send the shopper there instead of adding an unfinished line.
*/
(function () {
  if (window.LunicoraEasify) return;

  const CHOICE_TYPES = ['color-swatches', 'image-swatches', 'buttons', 'radio-buttons', 'dropdown', 'select', 'swatches', 'checkbox'];
  const TEXT_TYPES = ['text-box', 'textarea', 'number'];

  const parseJsonSetting = (value, fallback) => {
    if (typeof value !== 'string') return value ?? fallback;
    try {
      return JSON.parse(value);
    } catch (_error) {
      return fallback;
    }
  };

  const normalizeResourceId = (value) => String(value || '').split('/').pop();

  const productMatchesCondition = (product, condition) => {
    const conditionType = String(condition?.select || '').toUpperCase();
    const relative = String(condition?.relative || 'EQUAL').toUpperCase();
    const rawExpected = condition?.value;
    const expected = conditionType === 'COLLECTION'
      ? normalizeResourceId(rawExpected?.id || rawExpected)
      : String(rawExpected?.title || rawExpected?.name || rawExpected || '').trim();
    let actualValues = [];

    if (conditionType === 'COLLECTION') {
      actualValues = (product.collectionIds || []).map(normalizeResourceId);
    } else if (conditionType === 'TAG') {
      actualValues = product.tags || [];
    } else if (conditionType === 'TITLE') {
      actualValues = [product.title];
    } else if (conditionType === 'VENDOR') {
      actualValues = [product.vendor];
    } else if (conditionType === 'TYPE' || conditionType === 'PRODUCT_TYPE') {
      actualValues = [product.productType];
    } else if (conditionType === 'PRODUCT' || conditionType === 'ID') {
      actualValues = [normalizeResourceId(product.id)];
    } else if (conditionType === 'HANDLE') {
      actualValues = [product.handle];
    }

    const normalizedExpected = expected.toLowerCase();
    const normalizedActualValues = actualValues.map((value) => String(value || '').trim().toLowerCase());
    const equals = normalizedActualValues.some((value) => value === normalizedExpected);
    const contains = normalizedActualValues.some((value) => value.includes(normalizedExpected));
    const startsWith = normalizedActualValues.some((value) => value.startsWith(normalizedExpected));
    const endsWith = normalizedActualValues.some((value) => value.endsWith(normalizedExpected));

    if (relative.includes('NOT') && relative.includes('CONTAIN')) return !contains;
    if (relative.includes('NOT')) return !equals;
    if (relative.includes('CONTAIN')) return contains;
    if (relative.includes('START')) return startsWith;
    if (relative.includes('END')) return endsWith;
    return equals;
  };

  const productMatchesSet = (product, optionSet) => {
    if (!optionSet || Number(optionSet.status) !== 1) return false;

    if (Number(optionSet.apply_type) === 1) {
      const productId = normalizeResourceId(product.id);
      const explicitIds = (optionSet.option_set_products || []).map((item) => normalizeResourceId(item.product_id));
      const explicitHandles = parseJsonSetting(optionSet.products_handle, optionSet.products_handle || []);
      return explicitIds.includes(productId)
        || (Array.isArray(explicitHandles) && explicitHandles.includes(product.handle));
    }

    const conditionConfig = parseJsonSetting(optionSet.product_condition, {});
    const conditions = Array.isArray(conditionConfig?.conditions) ? conditionConfig.conditions : [];
    if (!conditions.length) return false;
    const results = conditions.map((condition) => productMatchesCondition(product, condition));
    return String(conditionConfig.operator || 'any').toLowerCase() === 'all'
      ? results.every(Boolean)
      : results.some(Boolean);
  };

  /*
    Returns [{ key, name, kind: 'choice' | 'text', sourceType, required,
    values: [{ label, color, image, isDefault }], placeholder, maxLength }].
    `key` is the property name the app submits with the line item
    (option_identifier, else the title), so the cart line ends up identical
    to one added from the product page.
  */
  const getCustomOptions = (product) => {
    const configurations = window.TPOConfigs?.product_options_easify;
    if (!Array.isArray(configurations) || !product) return [];

    const optionsByKey = new Map();
    configurations.forEach((configuration) => {
      const optionSet = configuration?.option_set;
      if (!productMatchesSet(product, optionSet)) return;

      (optionSet.options || []).forEach((option) => {
        if (Number(option.is_active_option) !== 1) return;
        const sourceType = String(option.type || '').toLowerCase();
        const kind = CHOICE_TYPES.includes(sourceType) ? 'choice' : (TEXT_TYPES.includes(sourceType) ? 'text' : null);
        if (!kind) return;

        const key = String(option.option_identifier || option.title || '').trim();
        const name = String(option.title || key).trim();
        if (!key || optionsByKey.has(key.toLowerCase())) return;

        const values = kind === 'choice'
          ? (option.option_types || []).map((value) => ({
            label: String(value.label || '').trim(),
            color: String(value.color_code || '').split(',')[0].trim(),
            image: String(value.image_url || '').trim(),
            isDefault: Number(value.is_default) === 1,
          })).filter((value) => value.label)
          : [];
        if (kind === 'choice' && !values.length) return;

        const maxLength = Number.parseInt(option.max_value, 10);
        optionsByKey.set(key.toLowerCase(), {
          key,
          name,
          kind,
          sourceType,
          required: Number(option.is_required) === 1,
          values,
          placeholder: String(option.placeholder || '').trim(),
          maxLength: Number.isFinite(maxLength) && maxLength > 0 ? maxLength : null,
        });
      });
    });

    return Array.from(optionsByKey.values());
  };

  /*
    Customily keeps a product's personalizer on its own servers; the same
    settings endpoint the app preloads on the product page answers for any
    product. A product "has a personalizer" when at least one option set is
    assigned to it. Results are cached per product for the session.
  */
  const customilyCache = new Map();
  const hasCustomilyPersonalizer = (product) => {
    const id = normalizeResourceId(product?.id);
    const handle = String(product?.handle || '').trim();
    const shop = window.Shopify?.shop;
    if (!id || !handle || !shop) return Promise.resolve(false);
    if (customilyCache.has(id)) return customilyCache.get(id);

    const storageKey = `lunicora-customily:${id}`;
    try {
      const stored = sessionStorage.getItem(storageKey);
      if (stored === '1' || stored === '0') {
        const known = Promise.resolve(stored === '1');
        customilyCache.set(id, known);
        return known;
      }
    } catch (_error) { /* storage unavailable: fall through to the request */ }

    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), 6000) : null;
    const request = fetch(`https://sh.customily.com/api/settings/unified/${encodeURIComponent(handle)}?shop=${encodeURIComponent(shop)}&productId=${encodeURIComponent(id)}`, {
      mode: 'cors',
      credentials: 'omit',
      signal: controller ? controller.signal : undefined,
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        const sets = Array.isArray(data?.sets) ? data.sets : [];
        const assigned = Array.isArray(data?.productConfig?.assignedSets) ? data.productConfig.assignedSets : [];
        const result = sets.length > 0 || assigned.length > 0;
        try { sessionStorage.setItem(storageKey, result ? '1' : '0'); } catch (_error) { /* ignore */ }
        return result;
      })
      .catch(() => false)
      .finally(() => { if (timer) clearTimeout(timer); });

    customilyCache.set(id, request);
    return request;
  };

  window.LunicoraEasify = { getCustomOptions, productMatchesSet, hasCustomilyPersonalizer };
})();
